import { useEffect, useId, useRef, useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { Field, fieldDescribedBy } from '@src/components/Field/Field';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { Typography } from '@src/components/Typography/Typography';
import './Select.css';

type NativeButtonProps = Omit<
  ComponentPropsWithRef<'button'>,
  'style' | 'type' | 'children' | 'value'
>;

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps extends NativeButtonProps {
  /** The chosen option's `value`. Empty shows the placeholder. */
  value: string;
  /** Underlined text trigger for compact inline controls. */
  inline?: boolean;
  options: SelectOption[];
  /** Optional selected-value copy, independent of menu option labels. */
  selectedLabel?: ReactNode;
  onValueChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  /** When omitted with `onOpenChange`, the list keeps its own open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  reserveSpace?: boolean;
  /** Parent id. The trigger, list and each option hang off it. */
  testId?: string;
}

export function Select({
  value,
  inline = false,
  options,
  selectedLabel,
  onValueChange,
  placeholder = 'Choose…',
  label,
  error,
  hint,
  open,
  onOpenChange,
  reserveSpace = false,
  className,
  disabled,
  id,
  onClick,
  onMouseDown,
  testId,
  ...rest
}: SelectProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const listId = `${controlId}-list`;
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = onOpenChange ? Boolean(open) : uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const selected = options.find((option) => option.value === value);
  const empty = !selected;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function onPointerDown(event: PointerEvent) {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, setOpen]);

  return (
    <Field
      controlId={controlId}
      label={label}
      error={error}
      hint={hint}
      disabled={disabled}
      reserveSpace={reserveSpace}
      testId={testId}
      className={['select', inline && 'select--inline', className].filter(Boolean).join(' ')}
    >
      <div className="select__anchor" ref={anchorRef}>
        <button
          ref={triggerRef}
          className="select__control"
          id={controlId}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={listId}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={fieldDescribedBy(controlId, { error, hint })}
          onMouseDown={onMouseDown}
          onClick={(event) => {
            setOpen(!isOpen);
            onClick?.(event);
          }}
          {...testIdAttr(testIdFor(testId, 'control'))}
          {...rest}
        >
          {empty ? (
            <span className="select__value select__value--placeholder">{placeholder}</span>
          ) : (
            <Typography as="span" role={inline ? 'meta/helper' : 'label/input'} className="select__value">
              {selectedLabel ?? selected.label}
            </Typography>
          )}
          <ChevronDownIcon
            className={isOpen ? 'select__arrow select__arrow--open' : 'select__arrow'}
          />
        </button>

        {isOpen && !disabled && (
          <ul
            className="select__list"
            id={listId}
            role="listbox"
            aria-labelledby={controlId}
            {...testIdAttr(testIdFor(testId, 'list'))}
          >
            {options.map((option) => {
              const isSelected = option.value === value;
              const classes = ['select__option'];
              if (isSelected) {
                classes.push('select__option--selected');
              }
              if (option.disabled) {
                classes.push('select__option--disabled');
              }

              return (
                <li key={option.value} role="none">
                  <button
                    className={classes.join(' ')}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onValueChange(option.value);
                      setOpen(false);
                      triggerRef.current?.focus({ preventScroll: true });
                    }}
                    {...testIdAttr(testIdFor(testId, `option-${option.value}`))}
                  >
                    <span className="select__option-copy">
                    <Typography
                      as="span"
                      role="label/input"
                      color="inherit"
                      className="select__option-label"
                    >
                      {option.label}
                    </Typography>
                    {option.description && <span className="select__option-description">{option.description}</span>}
                    </span>
                    {isSelected && <CheckIcon className="select__option-check" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Field>
  );
}
